<template>
  <main class='recipient-dropdown'>
    <sb-async-dropdown
      :selected="selected"
      :headerRows="headerRows"
      :rows="rows"
      :footerRows="footerRows"
      :focused="focused"
      @selectedRow="selectedRow"
      @deselectedRow="deselectedRow"
      @update:focused="updateFocused"
      class="dropdown"
      :class="{ 'no-preview': hidePreview, disabled: !!preSelectedFilters }"
    >
      <template #label-only>
        <div style="line-height:1.6rem">
          <slot />
        </div>
      </template>
    </sb-async-dropdown>

    <div v-if="!hidePreview" class="preview">
      <template v-for="(entry, index) in previewDatum">
        <span :key="entry.name">
          <span class='bold preview-name' style='margin-right:15px'>
            {{ entry.name }}
            <span v-if="entry.includeDateRange && hasDateRange">({{ startStr }} to {{ endStr }})</span>
            <span v-if="entry.hasSubfilter && subfilters[entry.name].start && subfilters[entry.name].end">
              ({{ formatDateToStr(subfilters[entry.name].start) }} to {{ formatDateToStr(subfilters[entry.name].end) }})
            </span>
            <span v-if="entry.hasSubfilter && (subfilters[entry.name].minSpend || subfilters[entry.name].maxSpend)">
              ({{ formatSpendStr(entry.name) }})
            </span>
            :
          </span>

          <span class='preview-labels' :class="{ 'not-last': index !== previewDatum.length - 1 }">
            {{ entry.labels.join(', ') }}
          </span>
        </span>
      </template>
    </div>
  </main>
</template>

<script>
import {groupsAPI, isSegmentChild, isTagChild} from "../../services/api/groupsAPI";
import {debounce} from 'lodash';
import {format} from 'date-fns';

const tagContextToPatchKeys = {
  'brands': 'brand_list',
  'categories': 'category_list',
  'interests': 'interest_list',
  'discounts': 'discount_list',
  'custom_groups': 'custom_group_list',
  'headsets': 'headset_list'
};

const groupsWithDateFilter = [
  'spending total', 'visiting total', 'average spend', 'order type'
];

/**
 * will order nested groups before the other group
 */
const nestedOrder = {
  'email activity': 'join date'
}

/**
 * Iterates over API response, aggregates "group" segments and then re-inserts them in a similar form to other entries
 */
const transformGroup = (groups) => {
  let result = groups.filter(group => !group.group);
  const grouped = groups.filter(group => !!group.group).reduce((result, group) => {
    if(!result[group.group]) {
      result[group.group] = [];
    }

    result[group.group].push(group);
    return result;
  }, {});

  Object.entries(grouped).forEach(([name, children]) => {
    const data = {
      type: 'nested',
      name,
      children
    };

    if(nestedOrder[name]) {
      const indexOfNext = result.findIndex((group) => group.name === nestedOrder[name]);
      result.splice(indexOfNext, 0, data);
    } else {
      result.push(data)
    }
  });

  return result;
}

export default {
  name: "recipient-dropdown",
  props: {
    groupsWithSelected: Object,
    hidePreview: {
      type: Boolean,
      default: false
    },
    preSelectedFilters: Object,
    showDeselectAll: {
      type: Boolean,
      default: false
    },
    showSubtext: {
      type: Boolean,
      default: true
    },
    triggerFocused: {
      type: Boolean,
      default: false
    },
    campaignId: {
      type: Number,
      default: null
    },
    autoCampaignId: {
      type: [Number, String],
      default: null
    }
  },
  data() {
    return {
      focused: false,
      childMap: new Map(),
      rootRows: [],
      previewDatum: [], // Array of tuples (group.name, Array.<string>)
      currentGroup: null,
      selected:[], //Holds all selected rows across all groups
      selectedLabelMap: new Map(), //Holds all selected label sets index by their group
      selectedLabelSet: new Set(), //Holds all selected labels for the currently rendered group (is null at root)
      selectedMap: new Map(), //Holds all selected sets indexed by their group
      selectedSet: new Set(), //Holds all selected items for the currently rendered group (is null at root)
      subfilters: {
        categories: {
          start: null,
          end: null,
          maxSpend: null,
          minSpend: null
        },
        brands: {
          start: null,
          end: null,
          maxSpend: null,
          minSpend: null
        }
      },
      start: null,
      end: null,
      rows: [],
      headerRows: [],
      footerRows: []
    }
  },
  created() {
    const { groups_with_selected: groups, tag_subfilters: tagSubfilters, sub_configuration: dateConfig } = this.groupsWithSelected;

    const transformedGroups = transformGroup(groups);

    this.rootRows = transformedGroups.map((group) => ({
      slot: 'parent-row',
      state: {
        context: group,
        label: group.name,
        onClick: () => {
          if(group.type === 'nested') {
            this.renderNestedParentRows(group);
          } else {
            this.renderChildren(group);
          }
        }
      }
    }));

    groups.forEach(async (group) => {
      this.selectedLabelMap.set(group.name, new Set(group.display_names));
      if(group.display_names) {
        // 1000 is number of elements per page
        // since we don't know on which page element is present
        const resp = await groupsAPI.getGroupChildren(group, 1, '', 1000);
        const childRows = this.buildToggleRows(resp.children);
        let selectedMap = new Set();
        childRows.forEach(row => {
          if(group.display_names.includes(row.state.label)) {
            selectedMap.add(row.state);
            this.selectedSet.add(row.state);
            this.selectedLabelSet.add(row.state.label);
            this.selected.push(row.state);
          }
        })
        this.selectedMap.set(group.name, selectedMap);
      }
    });

    if(tagSubfilters && tagSubfilters.length > 0) {
      tagSubfilters.forEach((tagSubfilter) => {
        this.subfilters[tagSubfilter.tag_type] = {
          end: tagSubfilter.max_date ? new Date(tagSubfilter.max_date + " 00:00:00") : null,
          start: tagSubfilter.min_date ? new Date(tagSubfilter.min_date + " 00:00:00") : null,
          maxSpend: tagSubfilter.max_spend ? +tagSubfilter.max_spend : null,
          minSpend: tagSubfilter.min_spend ? +tagSubfilter.min_spend : null
        }
      })
    }

    if(dateConfig && Object.keys(dateConfig).length > 0) {
      this.start = dateConfig.min_date ? new Date(dateConfig.min_date + " 00:00:00") : null;
      this.end = dateConfig.max_date ? new Date(dateConfig.max_date + " 00:00:00") : null;
    }

    this.renderRoot();
    this.updatePreview();
  },
  computed: {
    hasDateRange() {
      return this.start && this.end;
    },
    startStr() {
      return this.formatDateToStr(this.start);
    },
    endStr() {
      return this.formatDateToStr(this.end);
    }
  },
  methods: {
    formatDateToStr(date) {
      return date ? format(date, 'yyyy-MM-dd') : null;
    },

    formatSpendStr(type) {
      let str = ''
      const min = this.subfilters[type]?.minSpend,
        max = this.subfilters[type]?.maxSpend

      if (min && max) {
        str = `$${min} to $${max}`
      } else if (min) {
        str = `≥ $${min}`
      } else if (max) {
        str = `≤ $${max}`
      }

      return str
    },

    buildHeaderRows(group, nestedParent = null){
      const headerRows = [];

      if(group){
        headerRows.push({
          slot: 'go-back-row',
          state: {
            label: group.name,
            onClick: () => {
              if(nestedParent) {
                this.renderNestedParentRows(nestedParent);
              } else {
                this.renderRoot();
              }
            }
          }
        })
        if(!group.children || nestedParent){
          headerRows.push({
            slot: 'query-row',
            state: {
              value: '',
              update: debounce((newQuery) => {
                this.navigateRecipientComponent({ group, page: 1, query: newQuery }) //Page = 1 => API is not zero-indexed
              }, 500)
            }
          })
        }
      }

      if(this.showDeselectAll && !group){
        headerRows.push({
          slot: "button-row",
          state: {
            label: "deselect all",
            onClick: () => {
              const frag = this.resetFilters();
              this.updateGroups(frag);
            }
          }
        })
      }

      return headerRows;
    },

    updateDateWithSegments(value) {
      const { merchant_segment_ids } = this.getUpdateGroupsParams();
      this.updateGroups({
        merchant_segment_ids,
        date_range: value
      });
    },

    updateGroups: debounce(async function (frag = {}) {
      if(this.hasDateRange) {
        frag.date_range = `${this.startStr} to ${this.endStr}`;
      } else {
        frag.date_range = null;
      }

      if(Object.keys(frag).length > 0) {
        this.$emit('update:selected', frag);
        this.$emit("updatedSelectedGroups", this.previewDatum)
      }
    }, 500),

    async navigateRecipientComponent(options) {
      const { group, page, query } = options;
      this.rows = [
        'loading-row'
      ];

      if(!this.selectedMap.has(group.name)) {
        this.selectedMap.set(group.name, new Set());
      }

      this.selectedSet = this.selectedMap.get(group.name) || new Set();
      this.selectedLabelSet = this.selectedLabelMap.get(group.name) || new Set();

      const resp = await groupsAPI.getGroupChildren(group, page, query, 20);
      this.rows = this.createChildRows(resp.children);

      if(this.rows.length === 0) {
        this.rows = [
          {
            slot: 'text-row',
            state: {
              message: 'group not configured'
            }
          }
        ];
      }

      this.footerRows = [];

      // Do not show date range for AutoCampaign and Budz
      if(groupsWithDateFilter.includes(this.currentGroup.name) && !Number(this.autoCampaignId)) {
        this.footerRows.push({
          slot: 'sub-filter-row',
          state: {
            focused: this.hasDateRange,
            label: "add sub filters",
            filters: [
              {
                id: 'date-range-1',
                type: 'date-range',
                label: "select date range",
                start: this.start,
                end: this.end,
                maxDate: new Date(),
                update: (range) => {
                  if(!range) {
                    this.start = null;
                    this.end = null;
                    this.updateDateWithSegments(null)
                  } else if(range.start && range.end) {
                    this.start = range.start;
                    this.end = range.end;
                    this.updateDateWithSegments(`${this.startStr} to ${this.endStr}`)
                  }
                }
              }
            ]
          }
        });
      } else if (["categories", "brands"].includes(this.currentGroup.name) && this.campaignId) {
        this.footerRows.push(this.buildTagSubfilterRow(this.currentGroup.name));
      }

      if(resp.pagination.total_pages > 1) {
        this.footerRows.push(({
          slot: 'pagination-row',
          state: {
            page: page - 1, //API is not zero-indexed
            pages: resp.pagination.total_pages,
            gap: 2,
            variant: 'dotted',
            update: (newPage) => {
              //API is not zero-indexed
              this.navigateRecipientComponent({ group, page: newPage + 1, query })
            }
          }
        }));
      }
    },

    updateFocused(focused) {
      this.focused = focused;

      if(!focused) {
        this.renderRoot();
      }

      this.$emit('didUpdate:focused', this.focused);
    },

    getSelectedSize(row){
      const isParentRow = row.slot === 'parent-row',
            rowName = isParentRow ? row.state?.context?.name : row.name,
            selectedSet = this.selectedMap.get(rowName) || this.selectedLabelMap.get(rowName),
            size = selectedSet?.size,
            isNested = row.state?.context?.type && row.state?.context?.type === "nested";

      if(isNested) {
        const children = row.state.context?.children;
        let accumulator = 0;
        if(children) {
          children.forEach((child) => {
            const selectedLabelMapSize = this.selectedLabelMap.has(child.name) ? this.selectedLabelMap.get(child.name)?.size : 0,
                  selectedMapSize = this.selectedMap.has(child.name) ? this.selectedMap.get(child.name)?.size : 0;
            accumulator += selectedLabelMapSize || selectedMapSize;
          })
        }
        return accumulator || undefined;
      }
      return size || undefined;
    },

    renderNestedParentRows(group) {
      this.headerRows = this.buildHeaderRows(group);

      this.footerRows = [];

      this.rows = group.children.map((child) => {
        return {
          slot: 'parent-row',
          state: {
            context: child,
            label: child.name,
            badge: this.getSelectedSize(child),
            onClick: () => {
              this.renderChildren(child, group);
            }
          }
        }
      });
    },

    buildToggleRows(children){
      const hasMeta = this.$store.getters.advancedSegmentEnabled
      return children.filter((c) => {
        return c.meta_name == null || hasMeta
      }).map((child) => {
        // This childMap stores all previously gathered results
        // however the child.id could be a tag's id or a segment's id
        // therefore there is a possibility of overlapping ids if its within the first few merchants (segments and tags sharing small integers)
        // generally speaking, this is extremely unlikely for prod since tag ids and segment ids are separated by 3 orders of magnitude (100000 vs 100)

        if(!this.childMap.has(child.id)) {
          const state = {
            id: child.id,
            context: child,
            label: child.label,
            fetchedSubtext: false
          };

          if(this.showSubtext) {
            state.subText = '-- texts, -- app, -- emails'
          }

          this.childMap.set(child.id, ({
            slot: 'toggle-row',
            state
          }));
        }

        return this.childMap.get(child.id);
      });
    },

    buildTagSubfilterRow(type, focused = false) {
      return {
        slot: 'sub-filter-row',
        state: {
          focused: focused || this.hasSubfilter(type),
          label: "add sub filters",
          filters: [
            {
              id: 'date-range-1',
              type: 'date-range',
              label: "select date range",
              start: this.subfilters[type].start,
              end: this.subfilters[type].end,
              maxDate: new Date(),
              update: (range) => {
                if(!range) {
                  this.subfilters[type].start = null;
                  this.subfilters[type].end = null;
                  this.updateTagSubfilter(type);
                } else if(range.start && range.end) {
                  this.subfilters[type].start = range.start;
                  this.subfilters[type].end = range.end;
                  this.updateTagSubfilter(type);
                }
              }
            },
            {
              id: 'spend-range-1',
              type: 'spend-range',
              label: "amount spent",
              maxSpend: this.subfilters[type].maxSpend,
              minSpend: this.subfilters[type].minSpend,
              updateSpendSubfilter: (action, value) => {
                const newVal = value == "" ? null : value;
                let minVal, maxVal;

                switch (action) {
                  case "clear":
                    this.subfilters[type].maxSpend = null;
                    this.subfilters[type].minSpend = null;
                    break;
                  case "max":
                    minVal = +this.subfilters[type].minSpend; // either minSpend or 0 if minSpend is null
                    this.subfilters[type].maxSpend = !!newVal && (newVal < minVal) ? minVal : newVal
                    break;
                  case "min":
                    minVal = 0;
                    maxVal = this.subfilters[type].maxSpend; // can be null or string
                    
                    if (newVal && newVal < minVal) {
                      this.subfilters[type].minSpend = 0
                    } else if (newVal && maxVal && newVal > +maxVal) {
                      this.subfilters[type].minSpend = maxVal
                    } else {
                      this.subfilters[type].minSpend = newVal
                    }
                    break;
                }

                this.updateTagSubfilter(type);
              }
            }
          ]
        }
      }
    },

    createChildRows(children) {
      const rows = this.buildToggleRows(children);
      rows.forEach(async (row) => {
        if(this.selectedLabelSet.has(row.state.context.label) && !this.selectedSet.has(row.state)) {
          this.selectedSet.add(row.state);
          this.selected.push(row.state);
        }

        if(this.showSubtext && !row.state.fetchedSubtext) {
          row.state.fetchedSubtext = true;
          let result = await groupsAPI.count(row.state.context, this.campaignId);
          const pushesCounter = 'push' in result ? ` ${result.push} app,` : ""
          row.state.subText =`${result.sms} texts,${pushesCounter} ${result.email} emails`;
        }
      });

      return rows;
    },

    selectedRow(state) {
      this.selectedSet.add(state);
      this.selectedLabelSet.add(state.label);
      this.selected.push(state);

      this.didChangeSelected(state);
    },

    deselectedRow(state) {
      this.selectedSet.delete(state);
      this.selectedLabelSet.delete(state.label);
      this.selected = this.selected.filter((entry) => entry !== state);

      this.didChangeSelected(state);
    },

    async didChangeSelected(state) {
      const params = this.getUpdateGroupsParams();

      let updatedGroups = {};
      if(isSegmentChild(state.context)) {
        updatedGroups.merchant_segment_ids = params.merchant_segment_ids;
      }
      if(isTagChild(state.context)) {
        const tagContext = tagContextToPatchKeys[state.context.context];

        updatedGroups[tagContext] = params[tagContext];
        updatedGroups.merchant_tag_ids = params.merchant_tag_ids;
        updatedGroups.merchant_tags = params.merchant_tags;
      }

      await this.updateGroups(updatedGroups);
      this.updatePreview();
    },

    getUpdateGroupsParams() {
      //Build out the params
      const params = this.selected.reduce((result, state) => {
        if(isSegmentChild(state.context)) {
          result.merchant_segment_ids.push(state.context.id);
        } else if(isTagChild(state.context)) {
          const tagName = state.context.context,
                tagId = state.context.tag_id,
                key = tagContextToPatchKeys[tagName];

          if(!result[key]) {
            result[key] = [];
          }

          let tagIds = result.merchant_tags[tagName] || [];
          if(tagIds.includes(tagId)){
            tagIds = tagIds.filter(item => item !== tagId)
          }else{
            tagIds.push(tagId)
          }
          result.merchant_tags[tagName] = tagIds;

          result.merchant_tag_ids.push(tagId);
          result[key].push(state.context.tag);
        }

        return result;
      }, {
        merchant_tags: {},
        merchant_tag_ids: [],
        merchant_segment_ids: [],
        brand_list: [],
        category_list: [],
        interest_list: [],
        discount_list: [],
        custom_group_list: [],
        headset_list: []
      });

      //Map over params once last time to transform merchant taggings into comma delimited list
      const patchKeys = Object.values(tagContextToPatchKeys);
      for(const key in params) {
        if(patchKeys.includes(key)) {
          params[key] = params[key].join(',');
        }
      }
      return params
    },

    updatePreview() {
      if(!this.hidePreview){
        const datum = [];

        this.selectedLabelMap.forEach((selectedSet, groupName) => {
          const labels = Array.from(selectedSet.values())

          if(labels.length > 0) {
            datum.push({
              name: groupName,
              labels: labels,
              includeDateRange: groupsWithDateFilter.includes(groupName),
              hasSubfilter: ["categories", "brands"].includes(groupName) && this.hasSubfilter(groupName)
            });
          }
        });

        this.previewDatum = datum;
      }
    },

    updateTagSubfilter(type) {
      const frag = {
        tag_type: type,
        max_spend: this.subfilters[type].maxSpend,
        min_spend: this.subfilters[type].minSpend,
        max_date: this.formatDateToStr(this.subfilters[type].end),
        min_date: this.formatDateToStr(this.subfilters[type].start)
      }

      this.footerRows = this.footerRows.map((row) => {
        if (row?.slot === 'sub-filter-row') {
          return this.buildTagSubfilterRow(type, true)
        } else {
          return row
        }
      })

      this.$emit('update:tagSubfilter', frag);
      this.updatePreview();
    },

    hasSubfilter(type) {
      return (this.subfilters[type].start && this.subfilters[type].end) || this.subfilters[type].minSpend || this.subfilters[type].maxSpend
    },

    async renderChildren(group, nestedParent) {
      this.currentGroup = group;

      this.headerRows = [];

      await this.navigateRecipientComponent({ group, page: 1, query: '' }); //Page = 1 => API is not zero-indexed

      this.headerRows = this.buildHeaderRows(group, nestedParent);
    },

    renderRoot() {
      this.rootRows.forEach((row) => {
        row.state.badge = this.getSelectedSize(row);
      })
      this.headerRows = this.buildHeaderRows();
      this.rows = this.rootRows;
      this.footerRows = [];
    },

    async resetAndApplyPreSelectedFilters() {
      // Override and remove other pre-selected campaign filters
      const frag = this.resetFilters();

      if (!!this.preSelectedFilters) {
        // Add filters to update preview
        this.preSelectedFilters.groups_with_selected.forEach((group) => {
          this.selectedLabelMap.set(group.name, new Set(group.display_names));
        });

        // Structure params to update campaign with filters
        frag.merchant_segment_ids = this.preSelectedFilters.segments
        frag.category_list = this.preSelectedFilters.tags
      }

      this.renderRoot();
      await this.updateGroups(frag);
      this.updatePreview();
    },

    resetFilters(){
      const frag = {
        brand_list: '',
        category_list: '',
        custom_group_list: '',
        discount_list: '',
        interest_list: '',
        merchant_segment_ids: [],
        merchant_tag_ids: [],
        merchant_tags: []
      }

      // Reset variables that hold previously selected campaign filters
      this.selected = [];
      this.selectedLabelMap = new Map();
      this.selectedMap = new Map();
      this.rows.forEach(row => row.state.badge = 0);
      return frag
    }
  },
  watch: {
    triggerFocused(value){
      this.updateFocused(value);
    },

    preSelectedFilters() {
      this.resetAndApplyPreSelectedFilters()
    }
  }
}
</script>

<style scoped>
  .no-preview.dropdown {
    width: 100%;
  }

  .preview-name {
    margin-right: 15px;
  }

  .preview-labels.not-last{
    border-right: 1px solid #128ff9;
    padding-right: 10px;
    margin-right: 10px;
  }

  .recipient-row {
    display: flex;
    flex-direction: row;
    flex-wrap: nowrap;
    align-items: center;
  }

  .recipient-label {
    font-family: AileronThin;
    line-height: 1.6rem;
  }

  .recipient-dropdown {
    display: flex;
    flex-wrap: nowrap;
    border: 1px solid #128ff9;
    border-radius: 16px;
  }

  .preview {
    padding: 5px;
  }

  .recipient-dropdown >>> .sb-async-dropdown {
    margin:-1px;
  }

  .recipient-dropdown >>> .sb-async-dropdown.focused {
    min-width: 262px;
  }

  .recipient-dropdown >>> .sb-async-dropdown .sb-content {
    right: 0;
    left: 0;
  }

  .recipient-dropdown >>> .label-container {
    border-radius: 16px;
  }

  .recipient-dropdown >>> .sb-item-label {
    word-break: break-word;
  }

  .recipient-dropdown .sb-async-dropdown.focused >>> .label-container {
    border-radius: 16px 16px 0 0;
  }

  .recipient-dropdown .sb-async-dropdown.focused >>> .label-container.flatpickr-input {
    border-radius: 16px;
    font-size: 15px;
  }
  .recipient-dropdown .sb-async-dropdown.focused >>> .sb-content {
    border-radius: 0 0 16px 16px;
  }
  .recipient-dropdown .sb-async-dropdown.disabled >>> .label-container {
    background-color: #a4a4a4;
    pointer-events: none;
  }
</style>
